import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";

// Official full wordmark (blue #0a66c2 baked in) for the headline lockup.
import LinkedInWordmark from "@/assets/images/vectors/platforms/linkedin-wordmark.svg?react";
// LinkedIn isn't in simple-icons (removed at LinkedIn's request) — local fallback.
import LinkedInIcon from "@/assets/images/vectors/platforms/linkedin.svg?react";
import { GLASS_STYLE_LIGHT } from "@/common/ui/glass";
import { cn } from "@/common/utils";
import { Button } from "@/components/ui/Button";
import { OutboundCtaLink } from "@/landing/components/OutboundCtaLink";
import { signupCtaLabel } from "@/landing/lib/constants";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

const AVATARS = [1, 2, 3];

/* ------------------------------------------------------------------ */
/*  Agent rotator — crossfading icon + name inside the headline        */
/* ------------------------------------------------------------------ */

// Icon classes size each mark's VISIBLE glyph consistently and land it on
// the text baseline. The SVGs are full-bleed squares; the Claude Code PNG
// carries transparent padding (glyph ≈ 930×630 on an 1191×880 canvas), so it
// gets a taller box, a baseline drop matching its bottom padding, and no
// extra right margin (its own padding provides the gap).
const ROTATOR_AGENTS = [
  {
    name: "Claude Code",
    icon: "/icons/claude-code-bot.png",
    iconClassName: "mr-0 h-[1em] align-[-0.13em]",
  },
  {
    name: "Claude Cowork",
    icon: "/icons/claude.svg",
    iconClassName: "mr-[0.22em] h-[0.85em] align-baseline",
  },
  {
    name: "Codex",
    icon: "/icons/codex.svg",
    iconClassName: "mr-[0.22em] h-[0.85em] align-baseline",
  },
  {
    name: "OpenClaw",
    icon: "/icons/openclaw.svg",
    iconClassName: "mr-[0.22em] h-[0.85em] align-baseline",
  },
];

const ROTATE_INTERVAL_MS = 2500;
const ROTATE_FADE_MS = 350;

/**
 * Crossfades through the agent marks (same pattern as NewHero's
 * AgentRotator). It sits at the END of its headline line, so name-width
 * differences (Claude Cowork is widest) only move the line's ragged right
 * edge — the rest of the sentence never reflows.
 */
function AgentRotator() {
  const [index, setIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      setIsFading(true);
      timeout = setTimeout(() => {
        setIndex((i) => (i + 1) % ROTATOR_AGENTS.length);
        setIsFading(false);
      }, ROTATE_FADE_MS);
    }, ROTATE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // ROTATOR_AGENTS is fixed-length — modulo keeps index in bounds.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const agent = ROTATOR_AGENTS[index]!;

  return (
    <span
      className="inline-block align-baseline whitespace-nowrap transition-opacity duration-300"
      style={{ opacity: isFading ? 0 : 1 }}
    >
      {/* All four marks are white-fill assets and vanish on the warm light
          ground, so ink them with brightness(0) (same treatment as NewHero) to
          sit as ink marks next to the ink text. Every icon stays mounted
          (hidden) so it's decoded before its fade-in; the explicit
          vertical-align overrides preflight's `vertical-align: middle` so the
          mark sits on the text baseline. */}
      {ROTATOR_AGENTS.map((entry, entryIndex) => (
        <img
          key={entry.name}
          src={entry.icon}
          alt=""
          className={cn(
            "inline-block w-auto [filter:brightness(0)]",
            entry.iconClassName,
            entryIndex !== index && "hidden",
          )}
        />
      ))}
      {agent.name}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero graph — agents → WAB hub → LinkedIn surfaces                  */
/*  Same wiring-diagram language as WhyWonda's SchemaStage, restyled   */
/*  as light Steward tiles so it reads on the warm off-white ground.   */
/* ------------------------------------------------------------------ */

type GraphNode = {
  label: string;
  // percentage anchors that line up with the SVG viewBox endpoints below
  leftPct: string;
  topPct: string;
  content: ReactNode;
};

// Every agent mark is a white-fill asset, so each is inked with brightness(0)
// to read on the light tiles (same treatment as the headline rotator).
const AGENT_NODES: GraphNode[] = [
  {
    label: "Claude Code",
    leftPct: "12%",
    topPct: "10%",
    content: (
      <img
        src="/icons/claude-code-bot.png"
        alt=""
        className="size-6 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "Claude Cowork",
    leftPct: "12%",
    topPct: "36.5%",
    content: (
      <img
        src="/icons/claude.svg"
        alt=""
        className="size-6 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "Codex",
    leftPct: "12%",
    topPct: "63.5%",
    content: (
      <img
        src="/icons/codex.svg"
        alt=""
        className="size-6 [filter:brightness(0)]"
      />
    ),
  },
  {
    label: "OpenClaw",
    leftPct: "12%",
    topPct: "90%",
    content: (
      <img
        src="/icons/openclaw.svg"
        alt=""
        className="size-6 [filter:brightness(0)]"
      />
    ),
  },
];

const PLATFORM_NODES: GraphNode[] = [
  {
    label: "LinkedIn",
    leftPct: "88%",
    topPct: "50%",
    content: (
      // Brand blue on the light tile, matching the headline lockup's tile.
      <LinkedInIcon
        className="size-6"
        color="currentColor"
        style={{ color: "#0A66C2" }}
      />
    ),
  },
];

// viewBox is 0..300 x, 0..200 y. preserveAspectRatio="none" stretches it to
// fill, so these endpoints track the percentage-anchored HTML nodes above.
const WIRES = [
  { d: "M36 20 C 92 20, 108 100, 150 100", delay: 0 },
  { d: "M36 73 C 92 73, 112 100, 150 100", delay: 0.2 },
  { d: "M36 127 C 92 127, 112 100, 150 100", delay: 0.4 },
  { d: "M36 180 C 92 180, 108 100, 150 100", delay: 0.6 },
  { d: "M150 100 C 194 100, 210 100, 264 100", delay: 0.3 },
];

function GraphNodeTile({ node }: { node: GraphNode }) {
  return (
    // The TILE is what the wires aim at, so the tile — not the tile+label
    // stack — is what gets centred on the anchor. Centring the whole column
    // put the label's height into the reckoning and pushed every tile ~9px
    // above its wire, so the wires left through the bottom-right corner
    // instead of the middle of the right edge. The label hangs off the tile
    // instead, which also matches the hub (no label, already tile-centred).
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: node.leftPct, top: node.topPct }}
    >
      {/* Opaque ground fill + hairline: the same light-glass recipe the rest of
          the Steward landing uses, and the solid fill cleanly terminates the
          wire running underneath at the tile edge. */}
      <div
        className="flex size-11 items-center justify-center rounded-xl"
        style={GLASS_STYLE_LIGHT}
      >
        {node.content}
      </div>
      <span
        className="absolute top-full left-1/2 mt-1.5 -translate-x-1/2 font-mono text-[10px] tracking-tight whitespace-nowrap"
        style={{ color: "var(--steward-muted)" }}
      >
        {node.label}
      </span>
    </div>
  );
}

function HeroGraph({ animate }: { animate: boolean }) {
  return (
    <div className="relative h-[300px] w-full sm:h-[340px] lg:h-[380px]">
      {/* Wires + flowing pulses */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 300 200"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          {/* Edges muted grey, center green: the diagram converges on the WAB
              hub. The old white edge stops only read against dark footage; on
              #fafaf8 they'd vanish. These wires carry the meaning, so the end
              stops are held at 85% --steward-muted (~3.6:1 on the ground),
              clearing the 3:1 WCAG floor for graphical objects while staying a
              step lighter than the ~4:1 green centre so the convergence still
              reads. Anything lighter (--hair-strong, or the 55% this first
              relit to) drops the ends to ~2.2:1 and the wire dissolves before
              it reaches its tile. */}
          <linearGradient
            id="wab2-hero-wire"
            gradientUnits="userSpaceOnUse"
            x1="36"
            y1="0"
            x2="264"
            y2="0"
          >
            <stop
              offset="0"
              stopColor="color-mix(in srgb, var(--steward-muted) 85%, transparent)"
            />
            <stop offset="0.5" stopColor="var(--green)" />
            <stop
              offset="1"
              stopColor="color-mix(in srgb, var(--steward-muted) 85%, transparent)"
            />
          </linearGradient>
        </defs>
        {/* Full strength: the old 0.9 knocked white wires back off the dark
            footage, but on the light ground it only thins an already-faint
            line. */}
        {WIRES.map((wire) => (
          <path
            key={`base-${wire.d}`}
            d={wire.d}
            stroke="url(#wab2-hero-wire)"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {animate &&
          WIRES.map((wire) => (
            <motion.path
              key={`pulse-${wire.d}`}
              d={wire.d}
              stroke="var(--green)"
              strokeWidth={3}
              fill="none"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              pathLength={1}
              strokeDasharray="0.12 0.88"
              initial={{ strokeDashoffset: 1 }}
              animate={{ strokeDashoffset: [1, 0] }}
              transition={{
                duration: 1.9,
                ease: "linear",
                repeat: Infinity,
                delay: wire.delay,
              }}
            />
          ))}
      </svg>

      {/* Agent + platform nodes */}
      {AGENT_NODES.map((node) => (
        <GraphNodeTile key={node.label} node={node} />
      ))}
      {PLATFORM_NODES.map((node) => (
        <GraphNodeTile key={node.label} node={node} />
      ))}

      {/* Central WAB hub with green halo */}
      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
        style={{ left: "50%", top: "50%" }}
      >
        <div className="relative flex flex-col items-center">
          {/* On the dark footage this was additive light. Light ground inverts
              that: a translucent green disc behind a dark icon is a tinted
              shadow, not a glow, so the strength has to be set by what's
              legible rather than by what glowed before. The dark mix (55% at up
              to 0.95 opacity) reads as a hard smudge; merely attenuating it to
              28% took it to ~1.06:1, i.e. an invisible element still paying for
              an infinite animation. 45% across a 0.45-0.75 pulse lands at
              ~1.4-1.5:1 — the same soft presence as the clouds, so it reads as
              a deliberate glow at every point in the loop. */}
          {animate && (
            <motion.div
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-1/2 size-28 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in srgb, var(--green) 45%, transparent), transparent 68%)",
              }}
              animate={{
                opacity: [0.45, 0.75, 0.45],
                scale: [0.92, 1.04, 0.92],
              }}
              transition={{
                duration: 2.6,
                ease: "easeInOut",
                repeat: Infinity,
              }}
            />
          )}
          <img
            src={WabDockIcon}
            alt="Wonda Automated Browser"
            className="relative size-16 rounded-2xl"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Wab2Hero — the /linkedin hero (also used by the standalone Wonda Twin root).
 * Mirrors the main landing's split: this is the text hero on the warm
 * Steward ground, and the route pairs it with the separate framed video card
 * (HeroVideo) directly below. The left column carries the copy and the right
 * column shows the agents → WAB → LinkedIn wiring diagram.
 */
export function Wab2Hero() {
  const reducedMotion = useReducedMotion();

  return (
    <section className="relative overflow-hidden px-6 pt-10 pb-12 sm:px-8 md:pt-14 md:pb-16 lg:pt-16 lg:pb-20">
      {/* Decorative clouds — NewHero's treatment verbatim (brightness(0)
          flattens the dark webp to a silhouette, invert lifts it to a soft
          grey), at NewHero's size and offsets so both landings read as the
          same background. They sit at z-[5] under the z-10 content and land at
          ~#e8e8e7 on the ground. The content column is max-w-5xl so they have
          a real gutter to live in and stay clear of the copy and the diagram
          at xl; below that the viewport closes the gutter and they pass behind
          as ambient shape, which is what NewHero itself does once its own
          gutters run out. Ink over cloud still reads ~15:1 either way, and the
          diagram's tiles are opaque ground fills that punch through cleanly. */}
      <img
        src="/cloud-dark-left.webp"
        alt=""
        className="pointer-events-none absolute top-10 -left-20 z-[5] hidden w-64 opacity-60 [filter:brightness(0)_invert(0.86)] lg:block"
      />
      <img
        src="/cloud-dark-right.webp"
        alt=""
        className="pointer-events-none absolute top-40 -right-20 z-[5] hidden w-56 opacity-60 [filter:brightness(0)_invert(0.86)] lg:block"
      />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 mx-auto flex w-full max-w-5xl flex-col"
      >
        {/* Badge — light glass pill + César award, centered over the whole hero */}
        <div className="mb-10 flex justify-center">
          <div
            className="inline-flex items-center gap-2.5 rounded-full px-4 py-1.5 font-mono text-xs font-medium tracking-wide backdrop-blur-md"
            style={{ ...GLASS_STYLE_LIGHT, color: "var(--ink)" }}
          >
            <img
              src="/cesar-award.webp"
              alt="César award"
              className="h-5 w-auto opacity-80"
            />
            <span>AI Agent tool of 2026</span>
          </div>
        </div>

        <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-10">
          {/* Left column — copy */}
          <div className="flex flex-col items-start text-left">
            {/* Headline — Geist (the wonda.sh brand face). The type scale is
                intentionally NON-monotonic: sm..lg is single-column (full
                width), but at lg the grid splits into two columns and the
                headline's box halves to ~460px, relaxing back up at xl where
                the column reaches ~490px. Sizes are picked so "Let Claude
                Cowork" (the widest rotator entry) holds one line at every
                breakpoint — the rotator ends that line, so "use LinkedIn"
                never reflows as agents rotate. */}
            <h1
              className="text-[2rem] leading-tight font-semibold tracking-[-0.045em] sm:text-[3rem] lg:text-4xl xl:text-[3rem]"
              style={{
                fontFamily: '"Geist", var(--grotesk), sans-serif',
                color: "var(--ink)",
              }}
            >
              <span className="block">
                Let <AgentRotator />
              </span>
              <span className="block">
                use{" "}
                {/* The word rendered as the LinkedIn lockup: "Linked" in the
                    headline type, butted against the blue "in" tile. */}
                {/* Official LinkedIn wordmark (blue baked into the asset),
                    sized to the cap height and sitting on the baseline. The
                    tile's "in" is knocked out (transparent) in the official
                    file, so a white pad behind just the tile region gives the
                    canonical blue-tile / white-in read — the pad is what makes
                    the "in" white, so it stays white on the light ground too. */}
                {/* The wordmark's letters are 15 of the SVG's 22.26 units
                    (the tile is taller than the letters, per the official
                    logo), so scale the box to 1.07em to land the LETTERS at
                    the headline's cap height, and shift the whole lockup
                    (pad included) down 0.17em so the letter baseline (y=18 of
                    21.63) sits on the text baseline — the tile hangs slightly
                    below, as designed. */}
                {/* Official light-background lockup: "Linked" letters in the
                    headline ink, blue tile with white "in". The asset is one
                    currentColor path (letters end at x≈71%, tile starts at
                    ≈74.6%), so two stacked copies clipped at 73% carry the two
                    colors. */}
                <span className="relative inline-block translate-y-[0.17em] align-baseline">
                  <span className="sr-only">LinkedIn</span>
                  <span
                    aria-hidden
                    className="absolute rounded-[0.07em] bg-white"
                    style={{
                      left: "75.5%",
                      right: "1.5%",
                      top: "5%",
                      bottom: "5%",
                    }}
                  />
                  <LinkedInWordmark
                    aria-hidden
                    className="relative h-[1.07em] w-auto"
                    style={{
                      color: "var(--ink)",
                      clipPath: "inset(0 27% 0 0)",
                    }}
                  />
                  <LinkedInWordmark
                    aria-hidden
                    className="absolute inset-0 h-[1.07em] w-auto"
                    style={{
                      color: "#0A66C2",
                      clipPath: "inset(0 0 0 73%)",
                    }}
                  />
                </span>
              </span>
            </h1>

            {/* Subtitle — uniform IBM Plex Mono (the steward --mono face),
                single weight/color, mirroring the badge's premium mono. */}
            <p
              className="mt-9 max-w-xl font-mono text-[15px] leading-relaxed sm:text-base"
              style={{ color: "var(--cli-text-secondary)" }}
            >
              Every single flow you&apos;re doing manually, Claude can now do at
              scale, safely and in your voice.
            </p>

            {/* CTA — the default steward variant (dark ink on the light
              ground) is already the highest-contrast object here. */}
            <div className="mt-11">
              <Button asChild variant="steward" size="md">
                {/* Absolute to wonda.sh so signup always runs on the Wonda
                  identity origin. redirect_url drops
                  the user into the LinkedIn onboarding variant after signup. */}
                <OutboundCtaLink placement="hero">
                  {signupCtaLabel("Try now in seconds")}
                </OutboundCtaLink>
              </Button>
            </div>

            {/* Social proof */}
            <div className="mt-9 inline-flex items-center gap-2.5">
              <div className="flex shrink-0 items-center">
                {AVATARS.map((i, idx) => (
                  <img
                    key={i}
                    src={`/avatar-${i}.webp`}
                    alt=""
                    className="size-7 rounded-full border-2 object-cover"
                    style={{
                      borderColor: "var(--ground)",
                      marginLeft: idx === 0 ? 0 : "-0.8rem",
                    }}
                  />
                ))}
              </div>
              <span
                className="max-w-xl text-left text-sm"
                style={{ color: "var(--steward-muted)" }}
              >
                Join{" "}
                <span className="font-semibold" style={{ color: "var(--ink)" }}>
                  1,200+
                </span>{" "}
                founders &amp; GTM running their LinkedIn on custom workflows
              </span>
            </div>
          </div>

          {/* Right column — agents → WAB → LinkedIn wiring diagram */}
          <div className="w-full lg:pl-4">
            <HeroGraph animate={!reducedMotion} />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
